/**
 * useDisputes.ts
 *
 * Hook for the dispute/challenge system on settled prediction markets.
 * Tracks on-chain dispute state (via PredictionMarketMultiOracle events)
 * and provides helpers to raise or view disputes.
 *
 * NOTE: Full functionality requires the PredictionMarketMultiOracle contract
 * to be deployed and REACT_APP_MULTI_ORACLE_ADDRESS to be set.
 */

import { useState, useCallback } from 'react';
import { ethers } from 'ethers';

// ── ABI fragment ──────────────────────────────────────────────────────────────

const MULTI_ORACLE_ABI = [
  'function raiseDispute(uint256 marketId) external',
  'function getMarketState(uint256 marketId) view returns (tuple(uint256 yesVotes, uint256 noVotes, bool resolved, bool outcome, uint256 settledPrice, uint256 resolvedAt, bool disputed))',
  'event DisputeRaised(uint256 indexed marketId, address indexed challenger)',
];

// ── Config ────────────────────────────────────────────────────────────────────

const MULTI_ORACLE_ADDRESS =
  process.env.REACT_APP_MULTI_ORACLE_ADDRESS || '';

// ── Types ─────────────────────────────────────────────────────────────────────

export interface DisputeInfo {
  marketId:    number;
  yesVotes:    number;
  noVotes:     number;
  resolved:    boolean;
  outcome:     boolean;
  settledPrice: string;
  resolvedAt:  number;
  disputed:    boolean;
}

export type DisputeTxStatus = 'idle' | 'pending' | 'success' | 'error';

export interface UseDisputesResult {
  disputes:       DisputeInfo[];
  loading:        boolean;
  txStatus:       DisputeTxStatus;
  txError:        string | null;
  txHash:         string | null;
  fetchDispute:   (marketId: number) => Promise<DisputeInfo | null>;
  raiseDispute:   (marketId: number) => Promise<boolean>;
  resetTx:        () => void;
  isConfigured:   boolean;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function getProvider(): ethers.BrowserProvider {
  const eth = (window as any).ethereum;
  if (!eth) throw new Error('MetaMask not detected. Please install MetaMask.');
  return new ethers.BrowserProvider(eth);
}

// ── Hook ──────────────────────────────────────────────────────────────────────

export function useDisputes(): UseDisputesResult {
  const [disputes,  setDisputes]  = useState<DisputeInfo[]>([]);
  const [loading,   setLoading]   = useState(false);
  const [txStatus,  setTxStatus]  = useState<DisputeTxStatus>('idle');
  const [txError,   setTxError]   = useState<string | null>(null);
  const [txHash,    setTxHash]    = useState<string | null>(null);

  const isConfigured = Boolean(MULTI_ORACLE_ADDRESS);

  const fetchDispute = useCallback(async (marketId: number): Promise<DisputeInfo | null> => {
    if (!isConfigured) {
      console.warn('[useDisputes] REACT_APP_MULTI_ORACLE_ADDRESS not set');
      return null;
    }

    setLoading(true);
    try {
      const provider = getProvider();
      const contract = new ethers.Contract(MULTI_ORACLE_ADDRESS, MULTI_ORACLE_ABI, provider);
      const state    = await contract.getMarketState(BigInt(marketId));

      const info: DisputeInfo = {
        marketId,
        yesVotes:    Number(state.yesVotes),
        noVotes:     Number(state.noVotes),
        resolved:    state.resolved,
        outcome:     state.outcome,
        settledPrice: state.settledPrice.toString(),
        resolvedAt:  Number(state.resolvedAt),
        disputed:    state.disputed,
      };

      setDisputes(prev => {
        const filtered = prev.filter(d => d.marketId !== marketId);
        return [...filtered, info];
      });

      return info;
    } catch (err: any) {
      console.error('[useDisputes] fetchDispute error:', err);
      return null;
    } finally {
      setLoading(false);
    }
  }, [isConfigured]);

  const raiseDispute = useCallback(async (marketId: number): Promise<boolean> => {
    if (!isConfigured) {
      console.warn('[useDisputes] REACT_APP_MULTI_ORACLE_ADDRESS not set');
      return false;
    }

    setTxStatus('pending');
    setTxError(null);
    setTxHash(null);

    try {
      const provider = getProvider();
      const signer   = await provider.getSigner();
      const contract = new ethers.Contract(MULTI_ORACLE_ADDRESS, MULTI_ORACLE_ABI, signer);

      const txResp = await contract.raiseDispute(BigInt(marketId));
      setTxHash(txResp.hash);
      await txResp.wait();
      setTxStatus('success');

      // Refresh dispute state
      await fetchDispute(marketId);
      return true;
    } catch (err: any) {
      setTxStatus('error');
      setTxError(err?.message ?? 'Unknown error');
      return false;
    }
  }, [isConfigured, fetchDispute]);

  const resetTx = useCallback(() => {
    setTxStatus('idle');
    setTxError(null);
    setTxHash(null);
  }, []);

  return {
    disputes,
    loading,
    txStatus,
    txError,
    txHash,
    fetchDispute,
    raiseDispute,
    resetTx,
    isConfigured,
  };
}
